import { createTheme } from '@mui/material';
import { RJSFSchema, RJSFValidationError, UiSchema } from '@rjsf/utils';

type AnyOfOneOffType = {
  type?: string;
  format?: string;
};

export const cleanSchema = (schema: any) => {
  const formSchema = JSON.parse(JSON.stringify(schema));
  if (formSchema.properties) {
    Object.keys(formSchema.properties).forEach((key) => {
      if (formSchema.properties[key].required) {
        delete formSchema.properties[key].required;
      }
      if (formSchema.properties[key].hidden) {
        delete formSchema.properties[key];
        return;
      }

      const oneOfOrAnyOf = formSchema.properties[key].oneOf || formSchema.properties[key].anyOf;

      if (oneOfOrAnyOf && Array.isArray(oneOfOrAnyOf) && (oneOfOrAnyOf.find((i) => i.format) || formSchema.properties[key].format)) {
        // Find the non-null type definition
        const nonNullType = oneOfOrAnyOf.find((item: any) => item.type !== 'null');
        const hasNullType = oneOfOrAnyOf.some((item: any) => item.type === 'null');

        if (nonNullType) {
          // preserve the structure of the non-null type
          const { type: nonNullTypeValue, ...otherProps } = nonNullType;
          Object.assign(formSchema.properties[key], otherProps);

          // set the type correctly - handle nullable types
          if (hasNullType && nonNullTypeValue) {
            formSchema.properties[key].type = [nonNullTypeValue, 'null'];
          } else if (nonNullTypeValue) {
            formSchema.properties[key].type = nonNullTypeValue;
          }

          const format = oneOfOrAnyOf.find((item: AnyOfOneOffType) => item.format === 'date-time')?.format;

          if (format) {
            formSchema.properties[key].type = oneOfOrAnyOf.filter((item: AnyOfOneOffType) => item.type).map((item: AnyOfOneOffType) => item.type);
            formSchema.properties[key].format = format;
          } else if (formSchema.properties[key].format === 'UserPicker') {
            formSchema.properties[key].type = oneOfOrAnyOf.filter((item: AnyOfOneOffType) => item.type).map((item: AnyOfOneOffType) => item.type);
          }

          delete formSchema.properties[key].anyOf;
          delete formSchema.properties[key].oneOf;
        }
      }
    });
  }
  return formSchema;
};

export const getUiSchema = (schema: RJSFSchema) => {
  const uiSchema: UiSchema = getFormatMappings(schema);
  if (schema.properties) {
    Object.keys(schema.properties).forEach((key) => {
      const property = schema.properties?.[key];
      if (!property) return; // Skip if property is undefined

      if (!uiSchema[key]) {
        uiSchema[key] = {};
      }
      if (property.ui_helper_text && property.description) {
        if (!uiSchema.properties) {
          uiSchema.properties = {};
        }
        if (!uiSchema.properties[key]) {
          uiSchema.properties[key] = {};
        }
        uiSchema.properties[key].ui_helper_text = undefined;
      }
      if (property.ui_helper_text) {
        uiSchema[key]['ui:help'] = property.ui_helper_text;
      }
      if (property.ui_placeholder) {
        uiSchema[key]['ui:placeholder'] = property.ui_placeholder;
      }
      if (property.anyOf || property.oneOf) {
        uiSchema[key]['ui:placeholder'] = '';
        uiSchema[key]['ui:title'] = property.title;
      }
      if (property.format) {
        const format = property.format;
        if (format === 'MultiLanguagePicker') {
          const title = key;
          uiSchema[key]['ui:widget'] = title.toLowerCase().includes('audio') ? 'AudioTrack' : 'TimedTextTracks';
        }
      }
    });
  }
  return uiSchema;
};

export const getCustomTheme = (theme: any, showFormTitle: boolean) =>
  createTheme(theme, {
    components: {
      ...theme.components,
      MuiTypography: {
        styleOverrides: {
          h5: {
            fontSize: '1.1rem',
            fontWeight: theme.typography.fontWeightBold,
            opacity: 0.8,
            paddingBottom: theme.spacing(1),
            '& + .MuiDivider-root': {
              display: 'none'
            }
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            userSelect: 'none',
            '&.Mui-error': {
              '& span': {
                color: 'inherit'
              }
            }
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .has-error': {
              '.MuiAutocomplete-root label': {
                color: theme.palette.error.main
              }
            },
            '& .field-boolean': {
              paddingTop: 4
            },
            '& #root__title': {
              display: showFormTitle ? 'block' : 'none'
            }
          }
        }
      },
      MuiTextField: {
        ...theme.components.MuiTextField,
        defaultProps: {
          variant: 'filled',
          multiline: true
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            maxWidth: 300
          },
          select: {
            background: 'none'
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiFilledInput-root': {
              paddingTop: theme.spacing(2.5),
              paddingBottom: 0
            }
          }
        }
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            // the form is cutting off the left side of this so the background looked bad
            opacity: 1,
            transition: 'ease opacity 250ms',
            '&:hover': {
              backgroundColor: 'transparent',
              opacity: 0.9,
              transition: 'ease opacity 250ms'
            }
          }
        }
      }
    }
  });

export const ONE_TO_NINE = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

export const transformErrors = (errors: RJSFValidationError[], formSchema: any) => {
  return errors.map((error: RJSFValidationError) => {
    const { name, schemaPath } = error;

    const getOriginalErrorMessage = (name: string) => {
      switch (name) {
        case 'required':
          return 'This field is required.';
        case 'enum':
          if (error.params.allowedValues) {
            return 'This field is required.';
          }
          break;
        case 'minItems':
          if (error.params.limit > 0) {
            const title = /'(.*)'/.exec(error.stack);
            const limit = error.params.limit <= 9 ? ONE_TO_NINE[error.params.limit - 1] : error.params.limit;
            return `Must contain at least ${limit} ${title && title.length > 1 ? title[1] : ''}`;
          }
          break;
        case 'minLength':
          if (error.params.limit === 1) {
            return 'This field is required.';
          }
          break;
        default:
          break;
      }
      return undefined;
    };

    const findMessageOrGetOriginal = (source: any) => {
      const override = source?.message ? source?.message?.[name as string] : undefined;
      if (override || override === '') {
        return override;
      } else if (name) {
        const originalError = getOriginalErrorMessage(name);
        if (originalError) {
          return originalError;
        }
        return error.message;
      }
    };

    // #/properties/audio_track/items/allOf/1/then/properties/language/not
    try {
      const address = (schemaPath || '').split('/').slice(2); // [ audio_track, items, allOf, ... ]
      const primaryPropertyObject = address.length > 0 && formSchema?.properties ? formSchema?.properties[address[0]] : {}; // formSchema.properties.audio_track
      let messageSource = primaryPropertyObject;

      if (primaryPropertyObject && address.includes('properties')) {
        if (primaryPropertyObject.properties) {
          messageSource = primaryPropertyObject.properties; // formSchema.properties.audio_track.properties
        } else {
          const refPath = primaryPropertyObject?.items?.$ref || primaryPropertyObject?.$ref; // formSchema.properties.audio_track.items.$ref
          const refObject = formSchema.$defs[refPath.split('/').slice(2)[0]]; // formSchema.$defs.IFLAudioTrack
          const secondaryProperty = address[address.indexOf('properties') + 1]; // language
          messageSource = refObject.properties[secondaryProperty]; // formSchema.$defs.IFLAudioTrack.properties.language
        }
      } else if (address.includes('properties')) {
        const defSource = formSchema.$defs[address[0]];
        const secondaryProperty = address[address.indexOf('properties') + 1];
        const propertyObject = defSource.properties;
        messageSource = propertyObject[secondaryProperty];
      }

      error.message = findMessageOrGetOriginal(messageSource);
    } catch (e) {
      console.error('Error transforming error message:', e);
    }

    return error;
  });
};

export function getFormatMappings(schema: RJSFSchema): UiSchema {
  const formatMappings: UiSchema = {
    'ui:submitButtonOptions': {
      norender: true
    }
  };

  // Helper function to resolve a $ref path
  function resolveRef(ref: string): any {
    const path = ref.replace('#/', '').split('/');
    let current = schema;
    for (const segment of path) {
      current = current[segment];
      if (!current) return null;
    }
    return current;
  }

  // Recursive function to extract format mappings with path tracking
  function extractFormats(obj: any, currentPath: string[] = []): void {
    if (!obj || typeof obj !== 'object') return;

    // Handle $ref
    if (obj.$ref) {
      const resolved = resolveRef(obj.$ref);
      if (resolved) {
        extractFormats(resolved, currentPath);
      }
      return;
    }

    // handle root level object with format
    if (obj.format && obj.type !== 'string') {
      // Build nested object structure
      let current = formatMappings;
      for (let i = 0; i < currentPath.length - 1; i++) {
        if (!current[currentPath[i]]) {
          current[currentPath[i]] = {};
        }
        current = current[currentPath[i]];
      }
      current[currentPath[currentPath.length - 1]] = {
        'ui:widget': obj.format
      };
    }

    // Handle allOf/anyOf/oneOf
    if (obj.allOf) {
      obj.allOf.forEach((item: any) => extractFormats(item, currentPath));
    }
    if (obj.anyOf) {
      obj.anyOf.forEach((item: any) => extractFormats(item, currentPath));
    }
    if (obj.oneOf) {
      obj.oneOf.forEach((item: any) => extractFormats(item, currentPath));
    }

    // Handle properties object
    if (obj.properties) {
      Object.keys(obj.properties).forEach((propName) => {
        const newPath = [...currentPath, propName];
        const propValue = obj.properties[propName];

        // Check if this property has a format
        if (propValue.format && propValue.type !== 'string') {
          // Build nested object structure
          let current = formatMappings;
          for (let i = 0; i < newPath.length - 1; i++) {
            if (!current[newPath[i]]) {
              current[newPath[i]] = {};
            }
            current = current[newPath[i]];
          }
          current[newPath[newPath.length - 1]] = {
            'ui:widget': propValue.format
          };
        }

        // Continue recursively
        extractFormats(propValue, newPath);
      });
    }

    // Handle array items
    if (obj.items) {
      extractFormats(obj.items, currentPath);
    }
  }

  // Start from root properties
  if (schema.properties) {
    Object.keys(schema.properties).forEach((propName) => {
      extractFormats(schema.properties[propName], [propName]);
    });
  }

  return formatMappings;
}
